import { Injectable } from '@angular/core';
import { concat, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AppService } from '../_shared/services/app.service';
import { PrintTypeLong } from '../_shared/models/print-type';

export interface AdditionalAttachment {
    id: string;
    name: string;
    url: string;
    mimetype: string;
}

interface TaskDetails {
    id: string;
    state: string;
}

interface DocumentFile {
    objectType: string;
    documentUri: string;
    filename: string;
    mimeType: string;
}

export interface PdfDownload {
    url: string;
    type: string;
}

export interface PdfDownloadResult {
    isError: boolean;
    downloads: PdfDownload[];
}

@Injectable({
    providedIn: 'root',
})
export class NextStepsDataService {
    constructor(private http: HttpClient, private appService: AppService) {}

    public fetchOfferPdf(
        sapDocumentId: string,
        printType: PrintTypeLong,
        usecase: string
    ): Observable<PdfDownloadResult> {
        // check if pdf is already available. if not, start generation process
        return this.tryDownload(sapDocumentId).pipe(
            concatMap(({ downloads }) =>
                downloads.length
                    ? of({ isError: false, downloads })
                    : this.fetchPrintOfferPdf(sapDocumentId, printType, usecase)
            )
        );
    }

    private fetchPrintOfferPdf(
        sapDocumentId: string,
        printType: PrintTypeLong,
        usecase: string
    ): Observable<PdfDownloadResult> {
        // first request to initiate task of generating pdf
        return this.createPdfTask(sapDocumentId, printType, usecase).pipe(
            delay(2000),
            // second request to check the state of pdf task (interval of 2000)
            concatMap(({ taskId }) => this.waitForTaskCompletion(taskId, sapDocumentId)),
            concatMap((result) =>
                this.notifySalesRep(sapDocumentId, usecase).pipe(
                    map(() => result),
                    catchError(() => of(result))
                )
            )
        );
    }

    private tryDownload(sapDocumentId: string): Observable<PdfDownloadResult> {
        return this.getFileList(sapDocumentId).pipe(
            map((list) => {
                const offers = list.filter((entry) => entry.objectType.startsWith('OFFER'));
                const downloads = offers.map((offer) => ({
                    url: `${environment.http.quotation}files/${sapDocumentId}/${offer.documentUri}`,
                    type: offer.objectType,
                }));
                return {
                    isError: false,
                    downloads,
                };
            })
        );
    }

    private waitForTaskCompletion(taskId: string, sapDocumentId: string) {
        return this.getTaskDetails(taskId).pipe(
            concatMap((res) => this.checkState(res.state, sapDocumentId)),
            retryWhen((error) =>
                error.pipe(delay(4000), take(15), (o) => concat(o, throwError('Retry limit exceeded!')))
            )
        );
    }

    private checkState(state: string, sapDocumentId: string): Observable<PdfDownloadResult> {
        if (['FINISHED_WITH_ERRORS', 'TERMINATED', 'FINISHED_ABORTED', 'FAILED'].includes(state)) {
            return of({ isError: true, downloads: [] });
        } else if (['FINISHED', 'SUCCEEDED'].includes(state)) {
            return this.tryDownload(sapDocumentId);
        } else {
            throw new Error();
        }
    }

    private createPdfTask(documentNumber: string, printType: string, usecase: string) {
        return this.createTask({
            documentNumber,
            printType,
            usecase,
            useExistingOfferPdf: false,
            sendEmailToSalesRep: false,
        });
    }

    private notifySalesRep(documentNumber: string, usecase: string) {
        return this.createTask({
            documentNumber,
            usecase,
            sendEmailToSalesRep: true,
            useExistingOfferPdf: true,
        });
    }

    private getFileList(sapDocumentId: string) {
        return this.http.get<DocumentFile[]>(`${environment.http.quotation}files/${sapDocumentId}`);
    }

    private getTaskDetails(taskId: string): Observable<TaskDetails> {
        return this.http.get<TaskDetails>(`${environment.http.quotation}offers/tasks/${taskId}`);
    }

    private createTask(payload: {
        documentNumber: string;
        usecase: string;
        useExistingOfferPdf: boolean;
        sendEmailToSalesRep: boolean;
        printType?: string;
    }) {
        const url = `${environment.http.quotation}offers/tasks`;
        return this.http.post<{ taskId: string }>(url, {
            type: 'EXPORT_OFFER',
            input: {
                ...payload,
                locale: this.appService.getLanguageAsLocale(),
            },
        });
    }

    public getAttachments(id: string, isLegacy: boolean): Observable<any> {
        const url = this.attachmentBaseUrl(id, isLegacy);
        return this.http.get(url);
    }

    public deleteAttachment(id: string, attachmentId: string, isLegacy: boolean): Observable<boolean> {
        const url = `${this.attachmentBaseUrl(id, isLegacy)}/${attachmentId}`;
        return this.http.delete<boolean>(url).pipe(map(() => true));
    }

    public downloadAttachment(id: string, documentUri: string, isLegacy: boolean) {
        return `${this.attachmentBaseUrl(id, isLegacy)}/${documentUri}`;
    }

    private attachmentBaseUrl(id: string, legacy: boolean) {
        return legacy
            ? `${environment.http.baseUrl}quote/${id}/revision/0/attachments`
            : `${environment.http.quotation}quotations/quote-assist/quotes/${id}/attachments`;
    }

    public getQuoteEmailText(
        id: string,
        language: string,
        country: string,
        isLegacy: boolean
    ): Observable<{ text: string }> {
        const params = { language, country };
        const url = isLegacy
            ? `${environment.http.baseUrl}quote/${id}/revision/0/mail-text`
            : `${environment.http.quotation}quotations/quote-assist/quotes/${id}/mail-text`;
        return this.http.get<{ text: string }>(url, { params });
    }

    getEmailRecipients(customerNumber: string) {
        return this.http.get(`${environment.http.companyUrl}${customerNumber}/contacts`);
    }

    public getAdditionalAttachments(language: string, country: string): Observable<AdditionalAttachment[]> {
        return this.http
            .get<AdditionalAttachment[]>(`${environment.http.quotation}files/offer/attachments`, {
                params: { application: 'quote-assist', language, country },
            })
            .pipe(map((attachments) => attachments.map((a) => ({ ...a, name: a.url.split('/').pop() }))));
    }

    public getExportTypes(salesOrg: string) {
        return this.http.get(`${environment.http.quotation}offers/export/types`, { params: { salesOrg } });
    }
}
